import { Link } from 'react-router-dom';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Heart, Loader2 } from 'lucide-react';

/**
 * Multi-reaction set used on posts, comments, and curated content.
 * Order = picker order.
 */
export const REACTIONS = [
  { type: 'love',      emoji: '❤️',  label: 'Love',      textClass: 'text-rose-500' },
  { type: 'like',      emoji: '👍',  label: 'Like',      textClass: 'text-blue-500' },
  { type: 'haha',      emoji: '😂',  label: 'Haha',      textClass: 'text-amber-500' },
  { type: 'wow',       emoji: '😮',  label: 'Wow',       textClass: 'text-amber-500' },
  { type: 'sad',       emoji: '😢',  label: 'Sad',       textClass: 'text-sky-500' },
  { type: 'celebrate', emoji: '🎉',  label: 'Celebrate', textClass: 'text-purple-500' },
];

export const REACTION_BY_TYPE = Object.fromEntries(REACTIONS.map(r => [r.type, r]));

/**
 * Resolve the current user's reaction on a thing (post / comment / curated item).
 * Treats legacy `liked_by` membership as a "love" reaction so historical data
 * lights up correctly without migration.
 */
export function getMyReaction(item, currentUserId) {
  if (!currentUserId) return null;
  if (item?.reactions && item.reactions[currentUserId]) return item.reactions[currentUserId];
  if ((item?.liked_by || []).includes(currentUserId)) return 'love';
  return null;
}

/**
 * ReactionBar — heart-with-picker + count, with hover popovers showing who
 * reacted. Used on feed posts, feed comments, and curated content tiles.
 *
 * Props:
 *   myReaction: 'love' | 'like' | ... | null
 *   totalCount: number
 *   reactors: [{user_id, name, profile_photo, title, institution, reaction_type}]
 *     — most-recent first. Populated lazily by the parent when the hover opens.
 *   onReact: (type) => void  — parent calls feedAPI.react / etc.
 *   onShowAll: () => void  — opens parent's full-list dialog
 *   onHoverOpen: () => void — parent populates `reactors` on first hover
 *   size: 'md' (default) | 'sm' for comment row
 */
export default function ReactionBar({ myReaction, totalCount = 0, reactors, onReact, onShowAll, onHoverOpen, size = 'md' }) {
  const myDef = myReaction ? REACTION_BY_TYPE[myReaction] : null;
  const sm = size === 'sm';
  const buttonClass = sm ? 'p-0.5' : 'p-1';
  const iconSize = sm ? 'w-4 h-4' : 'w-5 h-5';
  const emojiSize = sm ? 'text-base' : 'text-xl';
  const countSize = sm ? 'text-xs' : 'text-sm';

  return (
    <div className={`flex items-center gap-1.5 ${myDef ? myDef.textClass : 'text-slate-gray'}`}>
      <HoverCard openDelay={150} closeDelay={150} onOpenChange={(open) => { if (open) onHoverOpen?.(); }}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            onClick={() => onReact(myReaction || 'love')}
            className={`${buttonClass} rounded-full hover:bg-slate-100 transition-colors`}
            aria-label={myDef ? `Remove ${myDef.label} reaction` : 'React'}
          >
            {myDef ? (
              <span className={`${emojiSize} leading-none`} aria-hidden="true">{myDef.emoji}</span>
            ) : (
              <Heart className={iconSize} />
            )}
          </button>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 p-2" align="start">
          {/* Picker */}
          <div className="flex items-center gap-1 mb-2">
            {REACTIONS.map(r => (
              <button
                key={r.type}
                type="button"
                onClick={() => onReact(r.type)}
                title={r.label}
                aria-label={r.label}
                className={`text-2xl hover:scale-125 transition-transform p-1 rounded-md ${myReaction === r.type ? 'bg-slate-100' : ''}`}
              >
                <span aria-hidden="true">{r.emoji}</span>
              </button>
            ))}
          </div>
          {/* Recent reactors */}
          {totalCount > 0 && (
            <>
              <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide px-2 py-1">Reacted by</p>
              {reactors === undefined && (
                <div className="flex items-center gap-2 px-2 py-3 text-xs text-slate-gray"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading...</div>
              )}
              {(reactors || []).slice(0, 5).map(u => (
                <Link key={u.user_id} to={`/profile/${u.user_id}`} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50">
                  <div className="relative">
                    <Avatar className="w-7 h-7"><AvatarImage src={u.profile_photo} /><AvatarFallback className="bg-mint-fresh text-dark-slate text-[10px]">{u.name?.charAt(0)}</AvatarFallback></Avatar>
                    {u.reaction_type && (
                      <span className="absolute -bottom-1 -right-1 text-[11px] leading-none" aria-hidden="true">{REACTION_BY_TYPE[u.reaction_type]?.emoji}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-dark-slate truncate">{u.name}</p>
                    {(u.title || u.institution) && <p className="text-[11px] text-slate-gray truncate">{[u.title, u.institution].filter(Boolean).join(' · ')}</p>}
                  </div>
                </Link>
              ))}
              {totalCount > 5 && onShowAll && (
                <button onClick={onShowAll} className="text-xs text-emerald-700 font-medium px-2 py-1.5 hover:underline w-full text-left">
                  + {totalCount - 5} more — see all
                </button>
              )}
            </>
          )}
        </HoverCardContent>
      </HoverCard>

      {totalCount > 0 ? (
        <button
          type="button"
          onClick={onShowAll}
          className={`${countSize} hover:underline`}
          aria-label="See everyone who reacted"
        >
          {totalCount}
        </button>
      ) : (
        <span className={countSize}>0</span>
      )}
    </div>
  );
}
